import React, { useState, useEffect } from "react";
import { bookImageSrc, bookImageFallback } from "./config";
import "../styles/Navbar.css";
import { FaCog, FaUser, FaSignOutAlt, FaCreditCard, FaShoppingCart, FaTrash, FaHeart, FaBars, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const Navbar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updateData = () => {
      const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(storedCart);

      const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      setWishlistItems(storedWishlist);
      
      // Check if user is superadmin
      const userRole = localStorage.getItem("userRole");
      const interests = JSON.parse(localStorage.getItem("selectedInterests") || "[]");
      setIsSuperAdmin(userRole === "superadmin" || interests.includes("All"));
    };

    updateData();
    window.addEventListener("storage", updateData);

    return () => {
      window.removeEventListener("storage", updateData);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".cart-container")) {
        setIsCartOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleProfileClick = () => {
    navigate("/profile");
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    localStorage.removeItem("userMembership");
    localStorage.removeItem("selectedInterests");
    localStorage.removeItem("userRole");
    
    console.log("✅ User logged out successfully");
    
    setIsDropdownOpen(false);
    navigate("/login");
  };

  const getTotalPrice = () => {
    return cartItems.reduce(
      (total, item) => total + (parseFloat(item.price) || 0),
      0
    );
  };

  const removeFromCart = (index) => {
    const updatedCart = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("storage"));
  };

  return (
    <nav className="navbar">
      <div className="logo">
        <img src="/images/Skill_Master.png" alt="Skill Master Logo" className="logo-img" />
        <div>
          SKILL<span>MASTER</span>
          <div className="tagline">Care, Learn, Thrive</div>
        </div>
      </div>

      <div className="navbar-right">
        <ul
          className={`menu ${isMenuOpen ? "open" : ""}`}
          onClick={() => setIsMenuOpen(false)}
        >
          {/* Notes - Always visible (shows books/PDFs) */}
          <li onClick={() => navigate("/allpages")}>Notes</li>

          {/* Videos - Always visible */}
          <li onClick={() => navigate("/videos")}>Videos</li>
          
          {/* Presentations - Always visible */}
          <li onClick={() => navigate("/presentations")}>Presentations</li>
          
          {/* Courses - Always visible */}
          <li onClick={() => navigate("/CourseCard")}>Courses</li>
          
          {/* Internship - Always visible */}
          {isSuperAdmin && (
            <li onClick={() => navigate("/internship")}>Internship</li>
          )}

          {/* Mentorship - Only for Superadmin */}
          {isSuperAdmin && (
            <li onClick={() => navigate("/mentorship")}>Mentorship</li>
          )}
          
          {/* Partnership - Only for Superadmin */}
          {isSuperAdmin && (
            <li onClick={() => navigate("/partnership")}>Partnership</li>
          )}
        </ul>

        {/* Hamburger - Only visible on mobile */}
        <div className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </div>

        {/* Wishlist Button - Always visible */}
        <div
          className="wishlist-container"
          style={{ position: "relative", marginRight: "15px", cursor: "pointer" }}
          onClick={() => navigate("/wishlist")}
        >
          <FaHeart className="wishlist-icon" size={24} />
          {wishlistItems.length > 0 && (
            <span className="wishlist-count">{wishlistItems.length}</span>
          )}
        </div>

        {/* Cart Button - Always visible */}
        <div className="cart-container">
          <FaShoppingCart
            className="cart-icon"
            onClick={() => setIsCartOpen(!isCartOpen)}
          />
          {cartItems.length > 0 && <span className="cart-count">{cartItems.length}</span>}
          {isCartOpen && (
            <div className="cart-dropdown">
              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <p>Your cart is empty.</p>
                  <button onClick={() => { navigate("/book"); setIsCartOpen(false); }} className="shopping-btn">
                    Go Shopping
                  </button>
                </div>
              ) : (
                <>
                  <div className="cart-items">
                    {cartItems.map((item, index) => (
                      <div key={index} className="cart-item">
                        <img
                          src={bookImageSrc(item, 60, 80)}
                          onError={bookImageFallback(item, 60, 80)}
                          alt={item.pdf_name}
                        />
                        <div className="cart-info">
                          <p>{item.pdf_name}</p>
                          <span>₹{item.price || "Free"}</span>
                        </div>
                        <FaTrash
                          className="delete-icon"
                          onClick={() => removeFromCart(index)}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="cart-footer">
                    <h4>Total: ₹{getTotalPrice()}</h4>
                    <button onClick={() => { navigate("/cart"); setIsCartOpen(false); }} className="go-cart-btn">
                      Go to cart
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* User Dropdown - Always visible */}
        <div className="menu-icon" onClick={toggleDropdown}>
          <img
            src="/images/user.png"
            alt="User Profile"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              objectFit: "cover",
              cursor: "pointer",
            }}
          />
        </div>
        {isDropdownOpen && (
          <div className="dropdown-container">
            <ul className="dropdown-menu">
              {/* Settings - Only for Superadmin */}
              {/*{isSuperAdmin && (
                <li onClick={handleSettingsClick}>
                  <FaCog className="dropdown-icon" />Settings
                </li>
              )}*/}
              <li onClick={handleProfileClick}>
                <FaUser className="dropdown-icon" />Profile
              </li>
              {isSuperAdmin && (
                <li onClick={() => { navigate("/admin-config"); setIsDropdownOpen(false); }}>
                  <FaCog className="dropdown-icon" />Configuration
                </li>
              )}
              <li>
                <FaCreditCard className="dropdown-icon" />Subscription
              </li>
              <li onClick={handleLogout}>
                <FaSignOutAlt className="dropdown-icon" />Logout
              </li>
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;